/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2020-2022, Bulb Inc. For more information, please email contact@hellobulb.com.
 *                _
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
*/
import {
  ApplicationRef, ComponentFactoryResolver, ComponentRef, Injectable, Injector, Type,
} from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { ImageComponent } from 'src/app/image/image.component';
import { CkeImageComponent } from 'src/ckeditor/component/cke-image/cke-image.component';
import { AeroButtonComponent } from '../aero/aero-button/aero-button.component';
import { ImageCarouselComponent } from '../app/image-carousel/image-carousel.component';

/**
 * Service which allows dynamically injecting Angular components directly into the DOM.
 */
@Injectable({
  providedIn: 'root',
})
export class ComponentInjectorService {
  /**
   * Store all the references of the injected components so we can be sure to clean up correctly.
   */
  public componentReferences: Map<string, any> = new Map<string, any>();

  /**
   * Reference of the mutation observer.
   */
  private mutationObserver: MutationObserver;

  /**
   * Standard Constructor.
   *
   * @param resolver Resolver for locating Component Factories for components to inject.
   * @param injector Angular injector to use when dynamically instantiating components.
   * @param app Application reference so we can connect the component to the app for monitoring changes, events, etc.
   */
  constructor(
    private resolver: ComponentFactoryResolver,
    private injector: Injector,
    private app: ApplicationRef,
  ) {
  }

  /**
   * Inject a dynamically instantiated component into the DOM, using the given parentNode as the parent of the
   * component.
   *
   * @param componentType The type of component to instantiate.
   * @param inputs Input values to assign to the new component.
   * @param parentNode DOM node to use as the parent of the new component.
   * @param id is the block id of the image.
   * @returns Reference to the newly created and injected component.
   */
  private injectDynamicComponent<T>(componentType : Type<T>, inputs : any, parentNode : any, id: string) : ComponentRef<T> {
    const factory = this.resolver.resolveComponentFactory(componentType);

    this.mutationObserver = new MutationObserver((mutationList, observer) => {
      for (const mutation of mutationList) {
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          for (const node of mutation.addedNodes as any) {
            if (node === parentNode || node.contains(parentNode)) {
              this.removeDynamicComponent(id);
              const ref = factory.create(this.injector, [], parentNode);
              this.componentReferences.set(id, ref);
              ComponentInjectorService.assignInputs(ref, inputs);
              this.app.attachView(ref.hostView);
            }
          }
        }
      }
    });

    const ckEditorElements = $('ckeditor');

    if (ckEditorElements && ckEditorElements.length) {
      this.mutationObserver.observe(ckEditorElements[0], { childList: true, subtree: true });
    }

    return null;
  }

  /**
   * Disconnect the mutation observer.
   */
  public disconnectMutationObserver() {
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }
  }

  /**
   * Helper method for inserting an AeroButton.
   * Primarily used if the caller doesn't have access to component Type information.
   *
   * @param inputs Input values to assign to the new button.
   * @param parentNode DOM node to make the parent of the button.
   * @param id is the block id of the button.
   * @returns Reference to the newly created button component.
   */
  public injectAeroButton(inputs : any, parentNode : any, id: string) : ComponentRef<AeroButtonComponent> {
    return this.injectDynamicComponent(AeroButtonComponent, inputs, parentNode, id);
  }

  /**
   * Helper method for injecting image component.
   * Primarily used if the caller doesn't have access to component Type information.
   *
   * @param inputs Input values to assign to the new image.
   * @param parentNode DOM node to make the parent of the image.
   * @param id is the block id of the image.
   * @returns Reference to the newly created image component.
   */
  public injectImage(inputs : any, parentNode : any, id: string) : ComponentRef<ImageComponent> {
    return this.injectDynamicComponent(ImageComponent, inputs, parentNode, id);
  }

  /**
   * Helper method for inserting a bulbImage.
   * Primarily used if the caller doesn't have access to component Type information.
   *
   * @param inputs Input values to assign to the new image.
   * @param parentNode DOM node to make the parent of the image.
   * @param id is the block id of the image.
   * @returns Reference to the newly created image component.
   */
  public injectCkeImage(inputs : any, parentNode : any, id: string) : ComponentRef<CkeImageComponent> {
    return this.injectDynamicComponent(CkeImageComponent, inputs, parentNode, id);
  }

  /**
   * Helper method for inserting a bulbImageCarousel.
   * Primarily used if the caller doesn't have access to component Type information.
   *
   * @param inputs Input values to assign to the new image carousel.
   * @param parentNode DOM node to make the parent of the image carousel.
   * @returns Reference to the newly created image carousel component.
   */
  public injectImageCarousel(inputs : any, parentNode : any, id: string) : ComponentRef<ImageCarouselComponent> {
    return this.injectDynamicComponent(ImageCarouselComponent, inputs, parentNode, id);
  }

  /**
   * Assign input values to the component.
   *
   * @param ref Reference to the component.
   * @param inputs Plain javascript object containing input values to assign.
   */
  private static assignInputs<T>(ref : ComponentRef<T>, inputs : any) {
    if (inputs) {
      const props = Object.getOwnPropertyNames(inputs);
      for (const prop of props) {
        ref.instance[prop] = inputs[prop];
      }
    }
  }

  /**
   * Remove the dynamic component.
   *
   * @param blockId is the id of the block.
   */
  public removeDynamicComponent(blockId: string) {
    if (this.componentReferences.get(blockId)) {
      this.app.detachView(this.componentReferences.get(blockId).hostView);
      this.componentReferences.get(blockId).destroy();
      this.componentReferences.delete(blockId);
    }
  }

  /**
   * To remove all the dynamic components.
   */
  public removeAllDynamicComponents() {
    this.componentReferences.forEach((value: any, key: string) => {
      this.app.detachView(this.componentReferences.get(key).hostView);
      this.componentReferences.get(key).destroy();
      this.componentReferences.delete(key);
    });
  }
}
